import GradingState from '@/components/organisms/Grading/GradingState'
import { Button, Radio } from '@/components/atoms'
import { useMemo, useState, useEffect, useCallback } from 'react'
import { academyLearningScheduleApi } from 'services/api'

interface GradingFreeWritngProps {
  type?: 'scoreCard' | 'gradingCard'
  activity?: any
  responseJson?: string
  manualGradingResults?: Record<number, 'correct' | 'incorrect' | 'grading'>
  onManualGradingChange?: (lineIdx: number, status: 'correct' | 'incorrect' | undefined) => void
  teacherComments?: Record<number, string>
  onTeacherCommentChange?: (lineIdx: number, text: string) => void
  canUseAi?: boolean
  lockedManualKeys?: Set<string>
}

const DEFAULT_WRITING_COUNT = 6;
const MIN_WRITING_COUNT = 1;
const MAX_WRITING_COUNT = 10;

const normalizeWritingCount = (value: unknown): number => {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) {
    return DEFAULT_WRITING_COUNT;
  }
  const rounded = Math.round(numeric);
  return Math.min(MAX_WRITING_COUNT, Math.max(MIN_WRITING_COUNT, rounded));
};

const GradingFreeWritng = ({ type = 'scoreCard', activity, responseJson, manualGradingResults, onManualGradingChange, teacherComments, onTeacherCommentChange, canUseAi = true, lockedManualKeys }: GradingFreeWritngProps) => {
  // 활동 설정 파싱 (힌트, 키워드, 작성 개수)
  const config = useMemo(() => {
    let cfg: any = {};
    try { if (activity?.configJson) cfg = typeof activity.configJson === 'string' ? JSON.parse(activity.configJson) : activity.configJson; } catch {}
    const keyword: string = cfg.keyword || '';
    const writingCount: number | undefined =
      cfg?.writingCount === undefined || cfg?.writingCount === null || cfg?.writingCount === ''
        ? undefined
        : normalizeWritingCount(cfg.writingCount);
    const hints: string[] = typeof cfg.hint === 'string'
      ? cfg.hint.split(/[,\n]/).map((s: string) => s.trim()).filter(Boolean)
      : Array.isArray(cfg.hints) ? cfg.hints : [];
    return { keyword, writingCount, hints };
  }, [activity]);

  // 응답 파싱 (학생 입력 + 에러)
  const parsed = useMemo(() => {
    let response: any = {};
    try { if (responseJson) response = JSON.parse(responseJson); } catch {}
    const inputTexts: string[] = Array.isArray(response.inputTexts) ? response.inputTexts : [];
    const errors: string[] = Array.isArray(response.errors) ? response.errors : [];
    return { inputTexts, errors };
  }, [responseJson]);

  // 라인별 채점 상태 관리: 'correct' | 'incorrect' | 'grading'
  const createInitialLineStatuses = (inputTexts: string[]) => {
    const map: Record<number, 'correct' | 'incorrect' | 'grading'> = {};
    inputTexts.forEach((_, idx) => {
      // 초기 상태는 항상 채점 대기
      map[idx] = 'grading';
    });
    return map;
  };

  const [lineStatuses, setLineStatuses] = useState<Record<number, 'correct' | 'incorrect' | 'grading'>>(() => createInitialLineStatuses(parsed.inputTexts));
  const activityId = activity?.id != null ? String(activity.id) : null;
  const isLineLocked = useCallback((idx: number) => {
    if (!activityId) return false;
    const key = `fw-${activityId}-${idx}`;
    return lockedManualKeys?.has(key) ?? false;
  }, [activityId, lockedManualKeys]);
  const hasUnlockedLines = useMemo(() => {
    if (!parsed.inputTexts || parsed.inputTexts.length === 0) return false;
    return parsed.inputTexts.some((_, idx) => !isLineLocked(idx));
  }, [parsed.inputTexts, isLineLocked]);

  // 저장된 채점 결과가 있으면 복원
  useEffect(() => {
    if (manualGradingResults && Object.keys(manualGradingResults).length > 0) {
      setLineStatuses(manualGradingResults);
    } else {
      setLineStatuses(createInitialLineStatuses(parsed.inputTexts));
    }
  }, [parsed.inputTexts, manualGradingResults]);

  // 전체 정/오답 표기는 요구사항상 불필요하므로 계산하지 않음

  // AI 채점: errors 기준으로 일괄 채점
  const [aiLoading, setAiLoading] = useState(false);
  const buildAiTopicContext = () => {
    const parts: string[] = [];
    if (activity?.title) parts.push(`제목: ${activity.title}`);
    if (config.keyword) parts.push(`키워드: ${config.keyword}`);
    if (config.hints.length > 0) parts.push(`힌트: ${config.hints.join(', ')}`);
    return parts.join('\n');
  };

  const handleAiGrading = async () => {
    if (!hasUnlockedLines) return;
    if (!canUseAi) {
      alert('AI 채점은 플러스 요금제 전용입니다.');
      return;
    }
    try {
      const scheduleId = (window as any).__CURRENT_SCHEDULE_ID__;
      setAiLoading(true);
      const res = await academyLearningScheduleApi.aiGradeFreeWriting(scheduleId, {
        inputTexts: parsed.inputTexts || [],
        topic: buildAiTopicContext() || 'Free writing evaluation',
        hints: config.hints || []
      });
      const results = res?.data?.results || [];
      const next: Record<number, 'correct' | 'incorrect' | 'grading'> = {};
      parsed.inputTexts.forEach((_, idx) => {
        const r = results.find((x: any) => x.index === idx);
        if (!r) next[idx] = 'grading';
        else next[idx] = r.ok ? 'correct' : 'incorrect';
      });
      setLineStatuses(next);
      // 우측 요약 실시간 반영: 각 라인의 수동 채점 상태를 상위에 전달
      Object.entries(next).forEach(([k, v]) => {
        const i = Number(k);
        if (v === 'grading') onManualGradingChange?.(i, undefined);
        else onManualGradingChange?.(i, v as 'correct' | 'incorrect');
      });
      // AI 교정 내용 반영
      results.forEach((r: any) => {
        if (typeof r.index === 'number' && r.corrected) {
          onTeacherCommentChange?.(r.index, r.corrected);
        }
      });
    } catch (e) {
      alert('AI 채점에 실패했습니다.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleLineStatusChange = (idx: number, status: 'correct' | 'incorrect') => {
    if (isLineLocked(idx)) return;
    setLineStatuses(prev => ({ ...prev, [idx]: status }));
    onManualGradingChange?.(idx, status);
  };

  // 프리라이팅 결과 렌더링 함수
  const renderFreeWriting = () => {
    if (parsed.inputTexts.length === 0) {
      return <p className='text-2xl font-bold'>학생 입력이 없습니다.</p>;
    }
    return (
      <div className='flex flex-col gap-3'>
        {parsed.inputTexts.map((text, idx) => {
          const lineLocked = isLineLocked(idx);
          return (
          <div key={idx} className='flex flex-col gap-2 p-3 bg-white rounded-2xl border border-gray-100'>
            <div className='flex justify-between items-center'>
              <p className='text-2xl font-bold break-words'>{text || ' '}</p>
              <GradingState status={lineStatuses[idx] || 'grading'} />
            </div>
            {type === 'gradingCard' && !lineLocked && (
              <div className='flex justify-between items-center'>
                <div className='flex gap-4 items-center'>
                  <Radio label='정답' name={`freewriting-${idx}`} onChange={() => handleLineStatusChange(idx, 'correct')} checked={(lineStatuses[idx] || 'grading') === 'correct'} />
                  <Radio label='오답' name={`freewriting-${idx}`} onChange={() => handleLineStatusChange(idx, 'incorrect')} checked={(lineStatuses[idx] || 'grading') === 'incorrect'} />
                </div>
              </div>
            )}
            
            {(type === 'gradingCard' || (teacherComments && teacherComments[idx])) && (
                <div className='mt-2'>
                    <p className='text-xs font-bold text-gray-500 mb-1'>교사 코멘트 / 교정</p>
                    {type === 'gradingCard' && !lineLocked ? (
                        <textarea
                            className='w-full p-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:border-indigo-500'
                            rows={2}
                            placeholder='코멘트나 교정 내용을 입력하세요.'
                            value={teacherComments?.[idx] || ''}
                            onChange={(e) => onTeacherCommentChange?.(idx, e.target.value)}
                        />
                    ) : (
                        <div className='p-2 bg-gray-50 rounded-lg text-sm whitespace-pre-wrap border border-gray-200'>
                            {teacherComments?.[idx] || <span className='text-gray-400'>입력된 코멘트가 없습니다.</span>}
                        </div>
                    )}
                </div>
            )}
          </div>
          );
        })}
      </div>
    );
  };
  
  return (
    <div>
        <div className='flex flex-col gap-2'>
            <p className='text-lg font-bold'>프리라이팅</p>
            <div className='flex flex-col gap-2 justify-center items-center p-5 rounded-2xl border border-gray-200'>
            <p className='text-xl font-bold text-center'>제시어 : {config.keyword || '키워드 없음'}</p>
            {typeof config.writingCount === 'number' && (
              <p className='text-sm text-gray-500'>작성 개수: {config.writingCount}개</p>
            )}
            </div>
            
            <div className='flex flex-col gap-2 justify-center items-center p-5 bg-gray-50 rounded-2xl border border-gray-100'>
                <p className='font-bold text-indigo-500'>Hint{config.hints.length > 0 ? ` (${config.hints.length})` : ''}</p>
                <div className='flex flex-wrap gap-y-2 gap-x-4 justify-center items-center text-2xl font-bold'>
                    {(config.hints.length > 0 ? config.hints : ['힌트가 없습니다.']).map((h, i) => (
                      <p key={i}>{h}</p>
                    ))}
                </div>
            </div>

            <div className={`overflow-hidden rounded-2xl border border-gray-200`}>
                    <div className='flex justify-between items-center px-4 py-2 w-full'>
                        <p className='text-sm font-bold text-gray-500'>입력한 답</p>
                        <div className='flex items-center gap-3'>
                          {type === 'gradingCard' && hasUnlockedLines && canUseAi && (
                            <Button size='small' onClick={handleAiGrading} disabled={aiLoading}>
                              {aiLoading ? '채점중...' : 'AI 채점하기'}
                            </Button>
                          )}
                        </div>
                    </div>
                    <div className='flex flex-col gap-2 px-5 py-3 bg-gray-50'>
                        {renderFreeWriting()}
                    </div>
                </div>


        </div>
    </div>
  )
}

export default GradingFreeWritng